import type { AuthProvider } from '@refinedev/core';
import axios from 'axios';
import { axiosInstance, TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/dataProvider/axiosInstance';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Tente un refresh du token.
 * Retourne true si réussi, false sinon.
 */
async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;

  try {
    const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
      refresh_token: refreshToken,
    });

    const { token, refresh_token: newRefreshToken } = response.data;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

    return true;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return false;
  }
}

export const authProvider: AuthProvider = {
  /**
   * Login — POST /api/v1/auth/login
   * Stocke access token (1h) + refresh token (30j).
   */
  login: async ({ email, password }) => {
    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/login`, {
        email,
        password,
      });

      const { token, refresh_token } = response.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

      return { success: true, redirectTo: '/' };
    } catch {
      return {
        success: false,
        error: {
          name: 'Erreur de connexion',
          message: 'Email ou mot de passe incorrect.',
        },
      };
    }
  },

  /**
   * Logout — Nettoie les tokens et redirige vers /login.
   */
  logout: async () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return { success: true, redirectTo: '/login' };
  },

  /**
   * Check — Vérifie si l'utilisateur est authentifié.
   * Tente un refresh si le token est expiré.
   */
  check: async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      return { authenticated: false, redirectTo: '/login' };
    }

    try {
      await axiosInstance.get('/api/v1/me');
      return { authenticated: true };
    } catch {
      // Token potentiellement expiré → tenter refresh
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        return { authenticated: true };
      }
      return { authenticated: false, redirectTo: '/login' };
    }
  },

  /**
   * GetIdentity — Retourne les infos de l'utilisateur connecté.
   */
  getIdentity: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
      const response = await axiosInstance.get('/api/v1/me');
      const user = response.data;

      return {
        id: user.id,
        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        email: user.email,
        avatar: user.avatarUrl,
        roles: user.roles,
      };
    } catch {
      return null;
    }
  },

  /**
   * GetPermissions — Retourne les rôles pour l'accessControlProvider.
   */
  getPermissions: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
      const response = await axiosInstance.get('/api/v1/me');
      return response.data.roles as string[];
    } catch {
      return null;
    }
  },

  /**
   * OnError — Gestion globale des erreurs auth.
   */
  onError: async (error) => {
    const status = (error as { statusCode?: number }).statusCode;
    if (status === 401) {
      const refreshed = await tryRefreshToken();
      if (!refreshed) {
        return { logout: true, redirectTo: '/login' };
      }
      return { retry: true };
    }
    return {};
  },
};
